/*
 * Presenting Qualified Video Views.
 *
 * The metric exists because a campaign running CTV beside YouTube has no
 * single honest "video views" number, and picking one anyway is how Never
 * Stop Chasing came to show 28,135 on a flight that delivered 167,257
 * qualified video outcomes. The count itself is governed in metricBasis.js.
 * What lives here is the other half of the fix: making sure the number is
 * never shown WITHOUT the platforms that produced it.
 *
 * That pairing is the whole point. A campaign-level total with no attribution
 * is how the previous headline misled — it looked homogeneous, so readers
 * reasonably assumed it was. This module always emits the contribution rows
 * alongside the headline, and a surface that renders one renders both.
 *
 * There is no platform branching anywhere in here. Which platforms qualify,
 * which field each contributes and what that field is natively called are all
 * decided by QUALIFIED_VIDEO_SIGNAL_REGISTRY in the shared engine; this file
 * only formats what the engine already resolved. A `platformKey === "ctv"`
 * test in a React surface is exactly the drift this structure prevents.
 *
 * Byte-pinned across Cortex and Quantum.
 */

import { describeUnavailableBasis } from "./metricBasis";
import {
  formatCostPerCompletedView,
  formatCostPerCompletedViewPrecise,
} from "./costPerCompletedView";

const isPlainObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value);

/**
 * A qualified total, in full. "167,257" is the number the engine counted, and
 * the number a reader reconciles against a spend report; "167.3K" was a
 * reading of it that made them go looking for the real one. The exact value
 * also stays on the row's `count` for anything that needs to compute.
 */
export function formatQualifiedVideoCount(value) {
  if (value === null || value === undefined) return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return Math.round(numeric).toLocaleString("en-US");
}

export function formatQualifiedVideoRate(value) {
  if (value === null || value === undefined) return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return `${numeric.toFixed(2)}%`;
}

const PLATFORM_LABELS = Object.freeze({
  youtube: "YouTube",
  ctv: "CTV",
  connected_tv: "CTV",
  tiktok: "TikTok",
  meta: "Meta",
  facebook: "Facebook",
  instagram: "Instagram",
});

const normalizeKey = (value) =>
  String(value === null || value === undefined ? "" : value)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

export function qualifiedVideoPlatformLabel(platformKey) {
  const key = normalizeKey(platformKey);
  if (PLATFORM_LABELS[key]) return PLATFORM_LABELS[key];
  if (!key) return "";
  /* A platform the label table has not met yet still renders readably rather
     than as a raw key — it cannot contribute without a registry ruling, but
     it can appear in an exclusion list. */
  return key
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/**
 * Turn the canonical `qualifiedVideo` basis into rows a surface can render.
 *
 * Returns null when there is nothing governed to show, so a caller renders
 * nothing rather than an empty shell — an absent video story and a video
 * story worth zero are different claims.
 */
export function buildQualifiedVideoPresentation(qualifiedVideo) {
  if (!isPlainObject(qualifiedVideo)) return null;

  const contributions = Array.isArray(qualifiedVideo.contributions)
    ? qualifiedVideo.contributions.filter(
        (entry) => isPlainObject(entry) && entry.value !== null && entry.value !== undefined
      )
    : [];

  if (!contributions.length) return null;

  const rows = contributions.map((entry) => {
    const platformLabel = qualifiedVideoPlatformLabel(entry.platformKey);
    const rate = isPlainObject(entry.rate) ? entry.rate : null;
    return {
      platformKey: entry.platformKey,
      platformLabel,
      /*
       * "YouTube TrueView Views", "CTV Completed Views" — the provider's own
       * vocabulary, so a client reconciling against a platform's native
       * dashboard finds the same words. The native label comes from the
       * registry; this file never invents one.
       */
      countLabel: `${platformLabel} ${entry.nativeLabel}`,
      nativeLabel: entry.nativeLabel,
      count: entry.value,
      countText: formatQualifiedVideoCount(entry.value),
      /* Present only when the engine resolved a rate on THIS platform's own
         observation. A row never borrows a neighbour's rate to look complete. */
      rateLabel: rate ? `${platformLabel} ${rate.label}` : null,
      rate,
      rateText: rate ? formatQualifiedVideoRate(rate.value) : null,
      rateValue: rate ? rate.value : null,
    };
  });

  /*
   * The explainer names the platforms and their native signals, assembled
   * from what actually contributed. Writing the sentence down would make it
   * wrong the moment the campaign's mix changed, and a metric that explains
   * itself incorrectly is worse than one that does not explain itself.
   */
  const explainer = `Combines each video platform's qualified viewing signal: ${rows
    .map((row) => row.countLabel.toLowerCase())
    .join(" + ")}.`;

  /*
   * `complete: false` means a platform that COULD have contributed reported
   * nothing — the total is a floor on the campaign, not a census, and the
   * surface should say so rather than let a reader take it for the whole.
   */
  const incompleteNote =
    qualifiedVideo.complete === false
      ? "Reported by contributing platforms — inventory that reported no qualified signal is excluded rather than counted as zero."
      : null;

  return {
    label: qualifiedVideo.label || "Qualified Video Views",
    total: qualifiedVideo.value,
    totalText: formatQualifiedVideoCount(qualifiedVideo.value),
    definitionVersion: qualifiedVideo.definitionVersion || null,
    explainer,
    incompleteNote,
    rows,
  };
}

/**
 * The Qualified Video Views KPI tiles for a fixed-width strip.
 *
 * Platform Analytics has a KPI strip, not a panel — a row of equal cards with
 * one number each. The Campaign Insights layout does not fit, but the SEMANTICS
 * must be identical, so this builds tiles from the same presentation model
 * rather than re-deriving anything. Two surfaces reading one model is the
 * whole point; a Platform-Analytics-specific resolver would be how the two
 * pages start disagreeing about what a campaign's video delivery was.
 *
 * The strip previously showed a Video Views count beside a Video View Rate,
 * and that adjacency was load-bearing: a reader takes two neighbouring tiles
 * as numerator and rate of one measurement. Replacing only the count would
 * leave YouTube's 25.38% sitting beside a 173.5K total it does not describe —
 * a NEW version of the defect this work exists to remove. So the rate slot is
 * replaced too, by the native rates, each labelled with the platform that
 * measured it.
 */
export function buildQualifiedVideoKpiTiles(qualifiedVideo) {
  const presentation = buildQualifiedVideoPresentation(qualifiedVideo);
  if (!presentation) return null;

  /*
   * "CTV 144.2K · 98.30% VCR · YouTube 29.3K · 25.06% VVR" — composition AND
   * native rates, on the tile itself.
   *
   * The strip is a fixed eight-column grid. Giving each native rate its own
   * card was the clearer layout and produced NINE tiles, which wraps one
   * orphan onto a second row — so the rates ride in the detail line instead,
   * each still bound to the platform that measured it. What is never done is
   * the thing the extra tile was avoiding: printing one rate beside the
   * qualified total as though it described all of it.
   */
  const contributionSummary = presentation.rows
    .map((row) =>
      row.rateText
        ? `${row.platformLabel} ${row.countText} · ${row.rateText} ${row.rate.label}`
        : `${row.platformLabel} ${row.countText}`
    )
    .join(" · ");

  const tooltip = [
    "Qualified Video Views combines each platform's governed meaningful-view signal.",
    "",
    ...presentation.rows.map((row) =>
      row.rateText
        ? `${row.platformLabel}: ${row.count.toLocaleString("en-US")} ${row.nativeLabel} · ${row.rateLabel} ${row.rateText}`
        : `${row.platformLabel}: ${row.count.toLocaleString("en-US")} ${row.nativeLabel}`
    ),
  ].join("\n");

  return {
    count: {
      label: presentation.label,
      value: presentation.totalText,
      detail: contributionSummary,
      tooltip,
      incompleteNote: presentation.incompleteNote,
    },
    /*
     * One tile per platform that measured a rate, never a combined one. Two
     * tiles where the strip had one is the honest shape: there are two
     * measurements, and the previous single tile was only ever showing one of
     * them while appearing to describe both.
     */
    rates: presentation.rows
      .filter((row) => row.rateText)
      .map((row) => ({
        platformKey: row.platformKey,
        label: row.rateLabel,
        value: row.rateText,
        title: `${row.rateLabel} is measured on ${row.platformLabel}'s own ${row.nativeLabel.toLowerCase()} and impressions, and describes no other platform.`,
      })),
  };
}

/*
 * The one sentence a scoped surface shows when the platform it is scoped to
 * cannot state a qualified-view cost. Named after the FACT that is missing —
 * the platform reported no governed qualified views for the window, whether
 * because it has no governed signal at all or because the signal's count is
 * absent or zero. The deliberate parallel is COMPLETED_VIEWS_UNREPORTED_NOTE
 * on the CPCV tile: both name the missing denominator rather than implying
 * the window was quiet, and neither is ever replaced by a value inferred
 * from an unrelated metric.
 */
export const QUALIFIED_VIEWS_UNREPORTED_NOTE = "Qualified views not reported";

/**
 * The Cost per Qualified Video View tile.
 *
 * The strip's fifth slot, and the reason it is in the strip rather than in the
 * qualified tile's detail line: it is a campaign efficiency reading of the
 * same order as CTR, not a footnote about video. It sits BEFORE the qualified
 * count deliberately — a cost stated after the outcome it prices reads as
 * commentary on it; stated before, the row runs money, delivery, response,
 * cost, outcome, which is the order the strip already uses for spend and
 * impressions.
 *
 * ADJACENCY IS FINE HERE, AND THAT IS NOT A CONTRADICTION
 *
 * `buildQualifiedVideoKpiTiles` above explains why a rate tile may not sit
 * beside the qualified total: a reader takes neighbouring tiles as one
 * measurement's count and rate, and YouTube's VVR does not describe the whole
 * total. This tile is adjacent on purpose and the same reading is CORRECT —
 * the cost IS this exact total divided into this exact spend. Nothing is
 * implied that is not true.
 *
 * ROUNDING
 *
 * Stated to the cent, through the same formatter every other money figure on
 * the page uses. A qualified view costs cents, so the cent figure is coarse
 * (NSC's $0.0702 shows as $0.07) — the exact value rides in the tooltip, the
 * same split `formatCostPerCompletedViewPrecise` already exists for. Two
 * roundings of one kind of number is the parity drift the pinned twins exist
 * to prevent, so this borrows that formatter rather than declaring its own.
 */
export function buildCostPerQualifiedVideoViewTile(costPerQualifiedVideoView) {
  const basis = isPlainObject(costPerQualifiedVideoView) ? costPerQualifiedVideoView : null;
  const label = basis?.label || "CPQV";
  const longLabel = basis?.longLabel || "Cost per Qualified Video View";

  /*
   * The card is built whether or not there is a number. A strip that drops a
   * tile when its metric is unavailable collapses from eight cards to seven
   * and re-opens the layout defect this work closed — and it tells the reader
   * the metric does not exist rather than that this window could not state it.
   */
  const unavailable = describeUnavailableBasis(basis);
  if (!basis || unavailable) {
    return {
      key: "costPerQualifiedVideoView",
      label,
      value: "—",
      available: false,
      detail: unavailable?.label || null,
      tooltip: [longLabel, unavailable?.detail || null].filter(Boolean).join("\n\n"),
      unavailableReason: unavailable?.reason || null,
    };
  }

  /*
   * The tooltip names the arithmetic, because the numerator is NOT the Spend
   * tile beside it and a reader who assumes it is will not reproduce the
   * number. Saying "$12,402.66 across CTV, YouTube" is the difference between
   * a figure that looks wrong and one that explains its own scope.
   */
  const scope = (basis.contributingPlatformKeys || []).map(qualifiedVideoPlatformLabel).join(", ");
  /*
   * The arithmetic line needs all three of its parts.
   *
   * A basis normally carries its own numerator, denominator and contributors,
   * because it was folded from per-platform observations. It does NOT when a
   * surface has only the canonical published value and no per-channel evidence
   * to re-fold — and stating "— of spend across , divided by 0 qualified video
   * views" is worse than stating nothing: it reads as a real scope of zero.
   * The value is still true and still governed, so the tile keeps it and drops
   * only the sentence it cannot substantiate.
   */
  const hasProvenance =
    Number.isFinite(basis.numerator) && Number.isFinite(basis.denominator) && Boolean(scope);
  const tooltip = [
    `${longLabel} — ${formatCostPerCompletedViewPrecise(basis.value)} exactly for this window.`,
    basis.spendBasisLabel || null,
    "",
    hasProvenance
      ? `${formatCostPerCompletedView(basis.numerator)} of spend across ${scope}, divided by ${Number(
          basis.denominator
        ).toLocaleString("en-US")} qualified video views.`
      : null,
    "Spend from platforms with no qualified video signal is not in this number.",
  ]
    .filter((line) => line !== null)
    .join("\n");

  return {
    key: "costPerQualifiedVideoView",
    label,
    value: formatCostPerCompletedView(basis.value),
    available: true,
    /* The scope, on the face of the card. A cost whose numerator excludes
       inventory the Spend tile includes has to say so where it is read. */
    detail: scope ? `${scope} spend` : null,
    tooltip,
    spendBasis: basis.spendBasis || null,
    spendBasisLabel: basis.spendBasisLabel || null,
    unavailableReason: null,
  };
}

export default buildQualifiedVideoPresentation;
